import {
  Timestamp,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { QuickTalesRef, db, storage } from "../../../firebase";
import { getDownloadURL, ref, uploadBytes, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

export const handleFilterChipClick = (
  index,
  options,
  setOptions,
  setFilterOptions
) => {
  let updatedOptions = [...options]; // Clone the current state to avoid direct mutation
  if (updatedOptions[index].label === "All") {
    // If "All" is selected, update all options to reflect the change
    updatedOptions = updatedOptions.map((option, idx) => ({
      ...option,
      selected: idx === index ? true : false, // Select "All" and deselect others
    }));
    setFilterOptions([]); // Clear all filter options since "All" is selected
  } else {
    // For any option other than "All", update its selected state and ensure "All" is deselected
    updatedOptions[index].selected = !updatedOptions[index].selected;
    updatedOptions[0].selected = false; // Deselect "All"

    // Update the filter options based on the selected state
    const filterOptionsTemp = updatedOptions.reduce((acc, curr, idx) => {
      if (idx !== 0 && curr.selected) {
        // Skip "All" and include only selected options
        acc.push(curr.label);
      }
      return acc;
    }, []);

    setFilterOptions(filterOptionsTemp);
  }
  setOptions(updatedOptions); // Update the options state with the new changes
};

export const quickTalesSearch = (array, keyword) => {
  const searchTerm = keyword.toLowerCase();
  return array.filter((value) => {
    return (
      (value.title &&
        value.title.toLowerCase().match(new RegExp(searchTerm, "g"))) ||
      (value.id &&
        value.id.toLowerCase().match(new RegExp(searchTerm, "g"))) ||
      (value?.createdByData?.userName &&
        value?.createdByData?.userName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (value?.createdByData?.firstName &&
        value?.createdByData?.firstName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (value?.createdByData?.lastName &&
        value?.createdByData?.lastName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (value?.narratorData?.userName &&
        value?.narratorData?.userName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (value?.narrator &&
        value?.narrator.toLowerCase().match(new RegExp(searchTerm, "g")))
    );
  });
};

export const getFilterQuickTales = async (
  whoCanListenFilterOptions,
  statusFilterOptions,
  modifiedDateRange,
  setLoading,
  handleFilterModalClose,
  organization,
  status
) => {
  setLoading(true);

  const deduplicateAndFilterApproved = (arr, status) => {
    const uniqueIds = new Set();
    const uniqueAndApprovedDocs = [];
    arr.forEach((doc) => {
      // Check both for uniqueness and approved status
      if (status === "pending") {
        if (
          !uniqueIds.has(doc.id) &&
          doc.approvalStatus.status !== "approved"
        ) {
          uniqueIds.add(doc.id);
          uniqueAndApprovedDocs.push(doc);
        }
      }
      if (status === "approved") {
        if (!uniqueIds.has(doc.id) && doc.approvalStatus.status === status) {
          uniqueIds.add(doc.id);
          uniqueAndApprovedDocs.push(doc);
        }
      }
    });
    console.log("Filtered & Deduplicated Results:", uniqueAndApprovedDocs);
    return uniqueAndApprovedDocs;
  };

  try {
    let results = [];

    const statusOptions =
      statusFilterOptions.length > 0 ? statusFilterOptions : [null];
    const whoCanListenOptions =
      whoCanListenFilterOptions.length > 0 ? whoCanListenFilterOptions : [null];

    for (const status of statusOptions) {
      for (const whoCanListen of whoCanListenOptions) {
        let queries = [];

        if (status !== null) {
          queries.push(where("approvalStatus.status", "==", status));
        }

        if (whoCanListen !== null) {
          queries.push(where("status", "==", whoCanListen));
        }

        if (
          modifiedDateRange &&
          modifiedDateRange.startDate &&
          modifiedDateRange.endDate
        ) {
          queries.push(
            where("approvalStatus.updatedAt", ">=", modifiedDateRange.startDate)
          );
          queries.push(
            where("approvalStatus.updatedAt", "<=", modifiedDateRange.endDate)
          );
        }

        if (organization) {
          queries.push(where("organization", "==", organization));
        }

        if (queries.length > 0) {
          const q = query(QuickTalesRef, ...queries);
          const querySnapshot = await getDocs(q);

          const fetchDetailsPromises = querySnapshot.docs.map(async (doc) => {
            let docData = doc.data();
            const userTemp = await getSingleUser(docData.createdBy);
            const narratorTemp = await getSingleUser(docData.narrator);
            return {
              id: doc.id,
              ...docData,
              photoUrl: docData?.imageUrls?.[0] ?? docData?.photoUrl ?? "",
              createdByData: userTemp?.data() ? userTemp?.data() : null,
              narratorData: narratorTemp?.data() ? narratorTemp?.data() : null,
            };
          });

          const queryResults = await Promise.all(fetchDetailsPromises);
          results = results.concat(queryResults);
        }
      }
    }
    const deduplicatedResults = deduplicateAndFilterApproved(results, status);
    handleFilterModalClose(); // Assuming it's appropriate to close the modal here
    return deduplicatedResults;
    // let results = [];

    // // Start with a query that filters QuickTales by the specific organization
    // let baseQuery = QuickTalesRef;

    // if (organization) {
    //   baseQuery = query(baseQuery, where("organization", "==", organization));
    // }

    // // Apply 'Who Can Listen' filters
    // if (whoCanListenFilterOptions.length > 0) {
    //   whoCanListenFilterOptions.forEach((option) => {
    //     baseQuery = query(baseQuery, where("status", "==", option));
    //   });
    // }

    // // Apply 'QT Status' filters
    // if (statusFilterOptions.length > 0) {
    //   statusFilterOptions.forEach((option) => {
    //     baseQuery = query(
    //       baseQuery,
    //       where("approvalStatus.status", "==", option)
    //     );
    //   });
    // }

    // // Apply 'Modified Date' filters
    // if (
    //   modifiedDateRange &&
    //   modifiedDateRange.startDate &&
    //   modifiedDateRange.endDate
    // ) {
    //   baseQuery = query(
    //     baseQuery,
    //     where("approvalStatus.updatedAt", ">=", modifiedDateRange.startDate),
    //     where("approvalStatus.updatedAt", "<=", modifiedDateRange.endDate)
    //   );
    // }

    // const querySnapshot = await getDocs(baseQuery);

    // // Process each document
    // for (const doc of querySnapshot.docs) {
    //   const userTemp = await getSingleUser(doc.data().createdBy);
    //   const narratorTemp = await getSingleUser(doc.data().narrator);
    //   results.push({
    //     id: doc.id,
    //     ...doc.data(),
    //     createdByData: userTemp?.data() ? userTemp?.data() : {},
    //     narratorData: narratorTemp?.data() ? narratorTemp?.data() : {},
    //   });
    // }

    // // Deduplicate the results if necessary
    // const deduplicatedResults = deduplicateAndFilterApproved(results, status);
    // console.log("Deduplicated Results:", deduplicatedResults);

    // // Close the filter modal
    // handleFilterModalClose();

    // // Set loading to false at the end of the function
    // setLoading(false);

    // return deduplicatedResults;
  } catch (error) {
    console.error("Error filtering QuickTales:", error.message);
    setLoading(false);
    return []; // Return an empty array in case of error
  } finally {
    setLoading(false);
  }
};

// export const getFilterQuickTales = async (
//   whoCanListenFilterOptions,
//   statusFilterOptions,
//   // modifiedStatusDateFilterOptions,
//   modifiedDateRange,
//   setLoading,
//   handleFilterModalClose,
//   organization,
//   status
// ) => {
//   setLoading(true);

//   const deduplicateAndFilterApproved = (arr, status) => {
//     const uniqueIds = new Set();
//     const uniqueAndApprovedDocs = [];
//     arr.forEach((doc) => {
//       // Check both for uniqueness and approved status
//       if (status === "pending") {
//         if (
//           !uniqueIds.has(doc.id) &&
//           doc.approvalStatus.status !== "approved"
//         ) {
//           uniqueIds.add(doc.id);
//           uniqueAndApprovedDocs.push(doc);
//         }
//       }
//       if (status === "approved") {
//         if (!uniqueIds.has(doc.id) && doc.approvalStatus.status === status) {
//           uniqueIds.add(doc.id);
//           uniqueAndApprovedDocs.push(doc);
//         }
//       }
//     });
//     console.log("Filtered & Deduplicated Results:", uniqueAndApprovedDocs);
//     return uniqueAndApprovedDocs;
//   };

//   try {
//     // Define the reference to your QuickTales collection
//     let results = [];

//     // Start with a query that filters QuickTales by the specific organization
//     let baseQuery = query(
//       QuickTalesRef,
//       where("organization", "==", organization)
//     );

//     // Apply 'Who Can Listen' filters
//     whoCanListenFilterOptions.forEach((option) => {
//       baseQuery = query(baseQuery, where("status", "==", option));
//     });

//     // Apply 'QT Status' filters
//     statusFilterOptions.forEach((option) => {
//       baseQuery = query(
//         baseQuery,
//         where("approvalStatus.status", "==", option)
//       );
//     });

//     if (
//       modifiedDateRange &&
//       modifiedDateRange?.startDate &&
//       modifiedDateRange?.endDate
//     ) {
//       baseQuery = query(
//         baseQuery,
//         where("approvalStatus.updatedAt", ">=", modifiedDateRange.startDate),
//         where("approvalStatus.updatedAt", "<=", modifiedDateRange.endDate)
//       );
//     }
//     // Execute the query

//     const querySnapshot = await getDocs(baseQuery);

//     // Process each document
//     for (const doc of querySnapshot.docs) {
//       // console.log("results::::", doc.data());
//       const userTemp = await getSingleUser(doc.data().createdBy);
//       const narratorTemp = await getSingleUser(doc.data().narrator);
//       results.push({
//         id: doc.id,
//         ...doc.data(),
//         createdByData: userTemp?.data() ? userTemp?.data() : {},
//         narratorData: narratorTemp?.data() ? narratorTemp?.data() : {},
//       });
//     }

//     // Deduplicate the results if necessary
//     const deduplicatedResults = deduplicateAndFilterApproved(results, status);
//     console.log("Deduplicated Results:", deduplicatedResults);

//     // Close the filter modal
//     handleFilterModalClose();

//     // Set loading to false at the end of the function
//     setLoading(false);

//     return deduplicatedResults;
//   } catch (error) {
//     console.error("Error filtering QuickTales:", error);
//     setLoading(false);
//     return []; // Return an empty array in case of error
//   }
// };

export const getAllQuickTales = async (organizationId) => {
  try {
    const queryRef = query(
      QuickTalesRef,
      where("organization", "==", organizationId)
    );
    const response = await getDocs(queryRef);

    return response;
  } catch (error) {
    console.log("Error fetching data from Firestore:", error.message);
    // console.error("Error fetching data from Firestore:", error.message);
    return [];
  }
};
export const getSpecificUserQuickTales = async (userId) => {
  try {
    const queryRef = query(
      QuickTalesRef,
      where("createdBy", "==", userId)
    );
    const response = await getDocs(queryRef);

    return response;
  } catch (error) {
    console.log("Error fetching data from Firestore:", error.message);
    // console.error("Error fetching data from Firestore:", error.message);
    return [];
  }
};

export const getSingleQuickTale = async (id) => {
  try {
    return await getDoc(doc(db, "quicktales", id));
  } catch (error) {
    console.log("error getting quicktale:", error.message);
  }
};
export const getSingleUser = async (userId) => {
  try {
    return await getDoc(doc(db, "users", userId));
  } catch (error) {
    // console.log("error getting user:", error.message);
  }
};
export const getSingleAdmin = async (adminId) => {
  try {
    return await getDoc(doc(db, "admins", adminId));
  } catch (error) {
    // console.log("error getting user:", error.message);
  }
};

export const deleteQuickTale = async (qtId) => {
  const quicktaleDocRef = doc(db, "quicktales", qtId);
  return await deleteDoc(quicktaleDocRef);
};

export const updateQuickTale = async (qtId, quicktale) => {
  const quicktaleDocRef = doc(db, "quicktales", qtId);
  return await updateDoc(quicktaleDocRef, quicktale);
};

export const createQuickTale = async (quicktaleData) => {
  try {
    const docRef = await addDoc(QuickTalesRef, {
      ...quicktaleData,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating quicktale:", error);
    throw error;
  }
};

export const duplicateQuickTale = async (qtId) => {
  try {
    const sourceSnap = await getDoc(doc(db, "quicktales", qtId));
    if (!sourceSnap.exists()) {
      throw new Error(`QuickTale not found: ${qtId}`);
    }

    const sourceData = sourceSnap.data();

    // Be explicit: ensure we never persist an old id field inside the duplicated doc.
    // (Firestore doc id will be auto-generated by addDoc.)
    const { id, ...rest } = sourceData ?? {};

    const duplicateData = {
      ...rest,
      status: 'unlisted',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const newDocRef = await addDoc(QuickTalesRef, duplicateData);
    return newDocRef.id;
  } catch (error) {
    console.error("Error duplicating quicktale:", error);
    throw error;
  }
};

export const uploadQuickTaleImageToStorage = async (file) => {
  const newFilename = uuidv4();
  const storageRef = ref(storage, `quicktales/images/${newFilename}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const uploadQuickTaleAudioToStorage = async (file) => {
  const newFilename = uuidv4();
  const storageRef = ref(storage, `quicktales/audio/${newFilename}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const uploadProcessedAudioToStorage = async (file) => {
  const newFilename = uuidv4();
  const storageRef = ref(storage, `processed-audio/quicktales/${newFilename}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};
